<template>
  <Slideover :visible="visible">
    <form @submit.prevent="addNewNote">
      <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
        <div class="flex items-start justify-between space-x-3">
          <h2 class="text-lg leading-7 font-medium">
            {{ title }}
          </h2>
          <div class="h-7 flex items-center">
            <button
              type="button"
              aria-label="Close panel"
              @click="$emit('toggle-slideover')"
              class="text-green-300 hover:text-white transition ease-in-out duration-150"
            >
              <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <!-- Main -->
      <div class="relative flex flex-1 flex-col py-3 px-2 mx-4">
        <div class="mb-3">
          <label
            for="event"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Event:
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              @change="updateForm($event)"
              type="text"
              id="event"
              list="events"
              :disabled="loadingEvent"
              class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            />
            <datalist id="events">
              <option v-for="event in events" :key="event">{{ event }}</option>
            </datalist>
            <div
              v-if="loadingEvent"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <LoadingSpinner class="text-gray-500" />
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label
            for="priority"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Priority:
          </label>
          <input
            @change="updateForm($event)"
            type="text"
            id="priority"
            list="priorities"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <datalist id="priorities">
            <option v-for="priority in priorities" :key="priority">{{
              priority
            }}</option>
          </datalist>
        </div>
        <div class="mb-3">
          <label
            for="followUp"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Follow Up:
          </label>
          <input
            @change="updateForm($event)"
            type="text"
            id="followUp"
            list="followUps"
            :disabled="loading"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <datalist id="followUps">
            <option v-for="followUp in followUps" :key="followUp.Text1">{{
              followUp.Text1
            }}</option>
          </datalist>
          <div
            v-if="loading"
            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
          >
            <LoadingSpinner class="text-gray-500" />
          </div>
        </div>

        <div class="mb-3">
          <label
            for="assignedTo"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Assign To:
          </label>
          <input
            @change="updateForm($event)"
            type="text"
            id="assignedTo"
            list="users"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
          <datalist id="users">
            <option v-for="user in users" :key="user.id"
              >{{ user.first_name }} {{ user.last_name }}</option
            >
          </datalist>
        </div>

        <label for="note">Note</label>
        <textarea
          id="note"
          name="note"
          class="border-2 p-3"
          style="width:100%"
          @change="updateForm($event)"
        ></textarea>
      </div>

      <HttpError :error="error" dismissable />

      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="
              $emit('toggle-slideover');
              $emit('notes-changed');
            "
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="submit"
            class="inline-flex justify-center items-center space-x-2 py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="submitting"
          >
            <span>Submit</span>
            <LoadingSpinner v-if="submitting" class="text-white" />
          </button>
        </span>
      </div>
    </form>
  </Slideover>
</template>
<script>
import Slideover from "@/components/layout/Slideover.vue";
import { ApiFactory } from "@/api/ApiFactory.js";
const NotesApi = ApiFactory.get("notes");
const ListingsApi = ApiFactory.get("listings");

export default {
  name: "AddNote",
  data() {
    return {
      form: {
        orderNumber: null,
        event: null,
        priority: null,
        assignedTo: null,
        followUp: null,
        note: "",
      },
      loading: false,
      loadingEvent: false,
      submitting: false,
      events: [
        "Address Verification",
        "Delay Shipping",
        "Dormancy Policy May 31",
        "Escalated Issue",
        "Fourwinds Tracking",
        "Fraud Review",
        "General Product Questions",
        "Order Confirmation Email Not Received",
        "Order Status",
        "Received Wrong Customers Order",
        "Tracking Email Not Received",
        "Warranty Notice Contacted Within 5 Days",
      ],
      priorities: ["EMAIL", "PHONE"],
      followUps: [
        "Review",
        "Activate Gift Card",
        "Cancelled Item",
        "Cancelled Order",
        "Charged Credit Card",
        "Refund Issued - Full",
        "Refund Issued - Partial",
        "Reship Item",
        "Store Credit Issued",
        "Warranty Issued",
      ],
      users: [],
      error: null,
    };
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    footer: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    orderNumber: {
      type: [String, Number],
      required: true,
    },
  },
  created() {
    this.loading = true;
    this.loadingEvent = true;
    ListingsApi.get({ list: "ADJ - Follow-up Tasks" }).then((response) => {
      this.followUps = response.data.body;
      this.loading = false;
    });
    ListingsApi.get({ list: "Note Events" }).then((response) => {
      this.events = response.data.body.map((entry) => entry.Text1);
      this.loadingEvent = false;
    });
  },
  components: {
    Slideover,
  },
  computed: {},
  methods: {
    addNewNote(event) {
      this.submitting = true;
      this.error = null;

      NotesApi.addNote(this.form)
        .then(() => {
          this.form = {
            orderNumber: null,
            event: null,
            priority: null,
            assignedTo: null,
            followUp: null,
            note: "",
          };

          event.target.reset();
          this.$emit("toggle-slideover");
          this.$emit("notes-changed");
        })
        .catch((error) => (this.error = error))
        .finally(() => (this.submitting = false));
    },
    updateForm(event) {
      let value = event.target.value;
      this.form[event.target.id] = value;
    },
  },
  mounted() {
    this.form.orderNumber = this.orderNumber;
  },
  filters: {},
};
</script>
